<template>
  <div>
    <div class="files-container">
      <div
        v-for="(file, i) in localFiles"
        :key="i"
      >
        <slot
          name="file-item"
          :file="file"
          :index="i"
          :viewFile="() => viewFile(file)"
          :removeFile="() => removeFile(i)"
        >
          <div
            class="file-panel"
          >
            <img src="@/assets/icons/file.svg" class="mr-3" />
            <span class="text--bold">{{ file.filename }}</span>
            <div class="flex-grow-1" />
            <b-spinner v-if="file.progress" :value="file.progress" small class="mr-3"></b-spinner>
            <div v-else>
              <!--          <img src="@/assets/icons/reload.svg" class="mr-3" />-->
              <img src="@/assets/icons/remove-red-eye.svg" class="mr-3" style="cursor: pointer" @click="viewFile(file)"/>
              <img src="@/assets/icons/trash-mdi.svg" class="mr-3" style="cursor: pointer" @click="removeFile(i)" />
            </div>
          </div>
        </slot>
      </div>
    </div>

    <input ref="fileInput" type="file" multiple style="display: none" @input="selectFileFromInput($event)" />
    <slot name="upload-box" :activateFileInput="activateFileInput">
      <div class="drop-zone"
           @click="activateFileInput"
           @dragover="$event.preventDefault()"
           @drop="$event.preventDefault(); dropFiles($event);"
      >
        {{label}}
      </div>
    </slot>
  </div>
</template>

<script>
import {v4 as uuidV4} from 'uuid';
import API from "@/api";
import Vue from "vue";
export default {
  name: "UploadManager",
  props: {
    label: {
      type: String,
      default: 'Select a file to upload'
    },
    files: {
      type: Array,
      default: () => ([])
    }
  },
  data(){
    return {
      localFiles: []
    }
  },
  watch: {
    files: {
      immediate: true,
      handler() {
        this.localFiles = JSON.parse(JSON.stringify(this.files))
      }
    }
  },
  methods: {
    activateFileInput(){
      this.$refs['fileInput'].click()
    },
    dropFiles(event) {
      if (event.dataTransfer.files.length) {
        const dropFile = event.dataTransfer.files[0];
        this.processFile(dropFile);
      }
    },
    selectFileFromInput() {
      if (this.$refs.fileInput.files.length) {
        const uploadFile = this.$refs.fileInput.files[0];
        this.processFile(uploadFile);
      }
    },
    processFile(file){
      let fileObj = {filename: file.name, progress: 1}

      const progress = (computable, loaded, total) =>
      {
        if(!computable) return;
        fileObj.progress = Math.round((loaded/total * 100));
      };

      this.localFiles.push(fileObj)

      this.secureUpload(file, progress)
        .then((file) => {
          Object.assign(fileObj, file);

          Vue.set(fileObj, 'filename', file.filename)
          Vue.delete(fileObj, 'progress');
          this.$emit('fileAdded', fileObj)
        })
        .catch(() => {
          const i = this.localFiles.findIndex(el => el === file);
          this.localFiles.splice(i, 1)
        })
    },
    secureUpload(file, progress){
      return new Promise((resolve, reject) => {
        const Key = uuidV4();
        const request = new XMLHttpRequest();
        let abortedPreUpload = false;

        API.getS3PUTURL(Key, file.type)
          .then((response) => {
              if(abortedPreUpload) return;
              // fieldName is the name of the input field
              // file is the actual file object to send
              const formData = new FormData();
              Object.keys(response.fields).forEach(key => {
                formData.append(key, response.fields[key]);
              });
              // Actual file has to be appended last.
              formData.append("file", file);

              request.open('POST', response.url);

              // Should call the progress method to update the progress to 100% before calling load
              // Setting computable to false switches the loading indicator to infinite mode
              request.upload.onprogress = (e) => {
                progress(e.lengthComputable, e.loaded, e.total);
              };

              // Should call the load method when done and pass the returned server file id
              // this server file id is then used later on when reverting or restoring a file
              // so your server knows which file to return without exposing that info to the client
              request.onload = function () {
                if (request.status >= 200 && request.status < 300) {
                  const {name: filename, type: mimetype, size} = file;
                  const {bucket} = response.fields;
                  const {url} = response;

                  API.createFile({
                    filename,
                    url: `${url}/${Key}`,
                    mimetype,
                    size,
                    bucket,
                    key: Key,
                  })
                    .then(({file}) => {
                      // the load method accepts either a string (id) or an object
                      resolve(file);
                    })
                    .catch(() => reject(new Error('Could not save uploaded file')));
                } else {
                  // Can call the error method if something is wrong, should exit after
                  reject(new Error('Could not upload file'));
                }
              };

              request.send(formData);
          })
          .catch((e) => {
            console.error('Upload error', e)
            reject(new Error('Could not upload file'));
          })
    }
)
    },
    viewFile(file){
      const { key } = file
      if(!key) return;

      API.getS3GETURL(key)
        .then(({signedURL}) => {
          API.downloadFile(signedURL, file.filename);
        })
        .catch((e) => {
          console.error(e)
        })
    },
    removeFile(index){
      // TODO: We need to actually remove the file from the files table and S3, not just the task
      const file = this.localFiles[index]
      this.localFiles.splice(index, 1)
      if(file.id) this.$emit('removeFile', file.id)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/SelfService/colors.scss';
@import '~@/styles/SelfService/spacing.scss';

.drop-zone {
  display: flex;
  justify-content: center;
  border: solid 1px #c6c6c6;
  border-radius: 5px;
  padding: 20px 30px;
  cursor: pointer;
  background-color: $background;
}

.files-container {
  .file-panel {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #FFFFFF;
    border: 1px solid $primary-disabled;
    box-sizing: border-box;
    border-radius: 12px;
    padding: 22px;

    &:first-child{
      margin-top: $spacing--l;
    }

    &:not(:last-child){
      margin-bottom: $spacing--m;
    }
  }
}
</style>
